import {
  Body,
  Group,
  H4,
  H6,
  Label,
  Stack,
  useWindowProperties,
} from '@qred/components-library';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SpinnerCircular from '~/components/shared/Spinner/SpinnerCircular';
import useTranslate from '~/hooks/useTranslate';
import { ApiStatus, RootState } from '~/store/types/sharedTypes';
import pushToGtmHelper from '~/helpers/pushToGtm.helper';
import { formatAmount } from '~/helpers/formatters.helper';
import { fetchAdditionalSignerCardApplication } from '~/store/actions/additionalSignerActions';
import CardAdditionalSignerFooter from './CardAdditionalSignerFooter';
import useQueryParams from '~/hooks/useQueryParams';
import { TOnboardingDTO } from '~/types/DTO/onboarding';
import { setAdditionalSignerCardApplicationData } from '~/store/slices/additionalSigner.slice';
import { defaultLanguageToMarket, setMarket } from '~/store/slices/intl.slice';
import { useHistory } from 'react-router';
import { numberFormatter } from '@qred/fe-cp-formatting-helpers';
import constVars from '~/constants/constVars';

const CardAdditionalSigner = () => {
  const dispatch = useDispatch();

  const {
    intl: { market },
    additionalSigner: { additionalSignerCardApplicationOffer, apiStatus },
  } = useSelector((state: RootState) => state);

  const translate = useTranslate();
  const queryParams = useQueryParams();
  const history = useHistory();

  const { isMobile } = useWindowProperties();

  const formatter = numberFormatter(market);

  useEffect(() => {
    // ============= REMOVE ONCE WE STOP SUPPORTING OLD SIGNER SOLUTION =============
    const base64ApplicationData = queryParams.get('data');
    if (base64ApplicationData) {
      if (market) {
        dispatch(setMarket(market));
        dispatch(defaultLanguageToMarket(market));
        pushToGtmHelper(`q_user_v_${market.toLowerCase()}`);
      }
      const cardOfferResponseData = JSON.parse(
        window.atob(base64ApplicationData)
      ) as TOnboardingDTO['getAdditionalSignerCardApplicationDataRes'];
      dispatch(setAdditionalSignerCardApplicationData(cardOfferResponseData));
      // ==============================================================================
    } else {
      dispatch(fetchAdditionalSignerCardApplication(market));
    }
  }, []);

  useEffect(() => {
    if (apiStatus.fetchAdditionalSignerCardApplication === ApiStatus.Failed) {
      history.push(constVars.ROUTE_SIGNER_ERROR);
    }
  }, [apiStatus.fetchAdditionalSignerCardApplication]);

  const cardDetailsTypographyAndValue = [
    {
      typography: translate('CreditCardDetailsCard.CompanyName') as string,
      value: additionalSignerCardApplicationOffer.companyName,
    },
    {
      typography: translate('CreditCardDetailsCard.ApprovedCredit') as string,
      value: formatAmount(
        additionalSignerCardApplicationOffer.offeredCreditLimit
      ),
    },
    {
      typography: translate('CreditCardDetailsCard.MonthlyInterest') as string,
      value: `${formatter(additionalSignerCardApplicationOffer.interest)} %`,
    },
    {
      typography: translate('CreditCardDetailsCard.YearlyMonthlyFee') as string,
      value: formatAmount(0),
    },
    {
      typography: translate('CreditCardDetailsCard.PlasticCardFee') as string,
      value: formatAmount(0),
    },
  ];

  return additionalSignerCardApplicationOffer.cardApplicationUuid ? (
    <>
      <Stack p={'xxl'} spacing={'xxl'}>
        <Stack spacing={'sm'}>
          <H4>{translate('ConfirmOffer')}</H4>
          <Body size="lg">
            {translate('CreditCardIntroductionCard.Title', {
              companyName: additionalSignerCardApplicationOffer.companyName,
            })}
          </Body>
        </Stack>
        <Stack spacing={'sm'}>
          <H6>{translate('CreditCardDetailsCard.Title')}</H6>
          {cardDetailsTypographyAndValue.map((cardDetail) => (
            <Group
              key={cardDetail.typography}
              spacing={2}
              justify="space-between"
              mb={2}
              direction={isMobile ? 'column' : 'row'}
            >
              <Body size="lg">{cardDetail.typography}</Body>
              <Label size="lg">{cardDetail.value}</Label>
            </Group>
          ))}
        </Stack>
      </Stack>
      <CardAdditionalSignerFooter />
    </>
  ) : (
    <SpinnerCircular />
  );
};

export default CardAdditionalSigner;
